import _extends from "@babel/runtime/helpers/extends";
/**
 * Copilot service functionality for handling AI responses
 *
 * This module provides utilities for interacting with the Copilot API,
 * processing streaming responses, and handling various response types including
 * text, data views, session updates, and action notifications.
 *
 * @module CopilotService
 * @example
 * import { getCopilotStreamAnswer, FEATURES, MODALITIES } from './copilot';
 *
 * getCopilotStreamAnswer({
 *   session: {
 *     features: [FEATURES.REPORTS],
 *     modalities: [MODALITIES.TEXT]
 *   },
 *   interactions: [{
 *     item: {
 *       content: { text: 'Hello, how can you help me?' }
 *     }
 *   }],
 *   setText: ({ text }) => console.log(text),
 *   onFinish: ({ text }) => console.log('Complete:', text)
 * });
 */
import { v4 as uuid } from 'uuid';
import { Fetch } from "./sFetch";
import { CHART_REGEX, TABLE_REGEX } from "../../shards/Copilot/hooks/constants";
import { getRegexMatch } from "../../shards/Copilot/hooks/utils";

// Response type constants for processing streaming events
const RESPONSE_TYPE = {
  SESSION_UPDATED: 'session.updated',
  TEXT_TYPE: 'response.text.delta',
  DATA_TYPE: 'response.content_part.done',
  PROGRESS_TYPE: 'response.content_part.added',
  PROGRESS_SUB_TYPE: 'progress',
  ERROR_TYPE: 'error',
  VIEW_SUB_TYPE: 'view',
  ACTION_DONE: 'response.action.done',
  ARTIFACT_CREATED: 'response.artifact.created'
};

// End of response marker indicating stream completion
const END_RESPONSE = '[DONE]';

/**
 * Available modalities for the copilot service
 * @readonly
 * @enum {string}
 */
export const MODALITIES = {
  /** Text-based interactions */
  TEXT: 'text',
  /** View/visual content interactions */
  VIEW: 'view'
};

/**
 * Available features aka "agents" that can be enabled for the copilot service
 * @readonly
 * @enum {string}
 */
export const FEATURES = {
  /** Report generation and analysis */
  REPORTS: 'reports',
  /** Vendor synchronization analysis */
  VENDOR_SYNC_ANALYZE: 'vendor_sync_analyze',
  /** Entity-specific chat functionality */
  ENTITY_CHAT: 'entity_chat',
  /** Knowledge base integration */
  KNOWLEDGE_BASE: 'knowledge-base',
  /** Sage copilot integration */
  SAGE_COPILOT: 'sage-copilot',
  /** Entity summary for GoldenMinute */
  ENTITY_SUMMARY: 'entity_summary',
  /** Prompt entity (p.e. email activities summarization) */
  PROMPT_ENTITY: 'prompt_entity',
  /** Related entities analysis (p.e. analyze activities related to the current entity from the activities widget) */
  ACTIVITIES_ANALYZE: 'activity_analyze',
  OPPORTUNITIES_ANALYZE: 'opportunities_analyze',
  SALES_ORDERS_ANALYZE: 'sales_orders_analyze'
};

/**
 * Available session types for the copilot service
 * @readonly
 * @enum {string}
 */
export const SESSION_TYPES = {
  /** Create a new session */
  CREATE: 'session.create',
  /** Update an existing session */
  UPDATE: 'session.update'
};

/**
 * Available interaction types for the copilot service
 * @readonly
 * @enum {string}
 */
export const INTERACTION_TYPES = {
  /** Standard message interaction */
  MESSAGE: 'message',
  /** Parameter-based interaction */
  PARAMS: 'params'
};

/**
 * Available interaction item types for the copilot service
 * @readonly
 * @enum {string}
 */
export const INTERACTION_ITEM_TYPES = {
  /** Create a conversation item */
  CREATE: 'conversation.item.create'
};

/**
 * Processes a streaming response from the Copilot API
 *
 * @param {Object} options - Stream processing options
 * @param {Function} options.resolve - Promise resolution function
 * @param {Response} options.response - Fetch response object with body stream
 * @param {Function} [options.setSession] - Callback for session updates, receives session object
 * @param {Function} options.setText - Callback for text updates, receives {text, responseId}
 * @param {Function} [options.setData] - Callback for data/view updates, receives {target, view}
 * @param {Function} [options.onFinish] - Callback when stream is complete, receives {text}
 * @param {Function} [options.onError] - Callback for error handling
 * @param {Function} [options.onAction] - Callback for action completion, receives action object
 * @param {Function} [options.onSetReader] - Callback to receive the stream reader instance
 * @returns {Promise} Promise that resolves when stream is processed
 */
const readStream = _ref => {
  let {
    resolve,
    response,
    setSession,
    setText,
    setData,
    onFinish,
    onError,
    onAction,
    onArtifact,
    onSetReader
  } = _ref;
  const reader = response.body.getReader();
  onSetReader === null || onSetReader === void 0 ? void 0 : onSetReader(reader);
  const decoder = new TextDecoder('utf-8');
  let text = '';
  let timeoutId = null;
  let isCanceled = false;

  /**
   * Processes text chunks from the stream
   *
   * @param {Object} chunk - Chunk data from the reader
   * @param {boolean} chunk.done - Whether the stream is complete
   * @param {Uint8Array} chunk.value - Binary data from the stream
   */
  const processText = async _ref2 => {
    let {
      done,
      value
    } = _ref2;
    if (done) return;
    try {
      const chunk = decoder.decode(value, {
        stream: true
      });

      // Split and filter in one pass
      const lines = chunk.split('\n\n').filter(line => line.trim()).map(preLine => {
        const obj = {};
        preLine.split('\n').forEach(current => {
          if (current.includes(`data: ${END_RESPONSE}`)) {
            obj.content = END_RESPONSE;
          } else if (current.startsWith('data:')) {
            try {
              _extends(obj, JSON.parse(current.substring(6)));
            } catch {
              // Invalid JSON - ignore this line
            }
          } else if (current.startsWith('event:')) {
            obj.event = current.substring(6).trim();
          } else if (current.startsWith('id:')) {
            obj.index = current.substring(4).trim();
          }
        });
        return obj;
      });
      for (const line of lines) {
        if (isCanceled) return resolve();
        if (line.content === END_RESPONSE) {
          clearTimeout(timeoutId);
          onFinish === null || onFinish === void 0 ? void 0 : onFinish({
            text
          });
          return resolve(text);
        }
        if (!line.type) continue;
        switch (line.type) {
          case RESPONSE_TYPE.ERROR_TYPE:
            onError === null || onError === void 0 ? void 0 : onError();
            return resolve();
          case RESPONSE_TYPE.SESSION_UPDATED:
            setSession === null || setSession === void 0 ? void 0 : setSession(line.session);
            break;
          case RESPONSE_TYPE.ARTIFACT_CREATED:
            const artifact = line.artifact;
            if (artifact !== null && artifact !== void 0 && artifact.type) {
              const {
                type,
                id,
                params
              } = artifact;
              switch (type) {
                case 'generate_chart':
                  {
                    const chartId = getRegexMatch(id, CHART_REGEX);
                    onArtifact === null || onArtifact === void 0 ? void 0 : onArtifact({
                      type: 'report',
                      payload: {
                        [chartId]: params
                      }
                    });
                    break;
                  }
                case 'generate_table':
                  {
                    const tableId = getRegexMatch(id, TABLE_REGEX);
                    onArtifact === null || onArtifact === void 0 ? void 0 : onArtifact({
                      type: 'table',
                      payload: {
                        [tableId]: params
                      }
                    });
                    break;
                  }
              }
            }
            break;
          case RESPONSE_TYPE.ACTION_DONE:
            onAction === null || onAction === void 0 ? void 0 : onAction(line.action);
            break;
          case RESPONSE_TYPE.PROGRESS_TYPE:
            const {
              part
            } = line;
            if ((part === null || part === void 0 ? void 0 : part.type) === RESPONSE_TYPE.PROGRESS_SUB_TYPE) {
              setText({
                text: `*${part.text}*...`,
                responseId: line === null || line === void 0 ? void 0 : line.response_id
              });
            }
            break;
          case RESPONSE_TYPE.DATA_TYPE:
            {
              var _part$view, _part$view$data;
              const {
                part
              } = line;
              if ((part === null || part === void 0 ? void 0 : part.type) === RESPONSE_TYPE.VIEW_SUB_TYPE && part !== null && part !== void 0 && (_part$view = part.view) !== null && _part$view !== void 0 && (_part$view$data = _part$view.data) !== null && _part$view$data !== void 0 && _part$view$data.length) {
                var _part$view2;
                const featureTarget = (_part$view2 = part.view) === null || _part$view2 === void 0 ? void 0 : _part$view2.view;
                const view = part.view;
                if (featureTarget && view) {
                  setData === null || setData === void 0 ? void 0 : setData({
                    target: featureTarget,
                    view
                  });
                }
              }
              break;
            }
          case RESPONSE_TYPE.TEXT_TYPE:
            text += line.delta;
            setText === null || setText === void 0 ? void 0 : setText({
              text,
              responseId: line === null || line === void 0 ? void 0 : line.response_id
            });
            break;
          default:
            break;
        }
      }

      // Continue reading
      const nextChunk = await reader.read();
      return processText(nextChunk);
    } catch (err) {
      console.error('Stream processing error:', err);
      onError === null || onError === void 0 ? void 0 : onError();
      resolve();
    }
  };
  return reader.read().then(processText);
};

/**
 * Makes a request to the Copilot API and processes the streaming response
 *
 * @param {Object} options - Request options
 * @param {Object} [options.session={}] - Session configuration object
 * @param {string} [options.session.event_id] - Unique event identifier for the session
 * @param {string} [options.session.type] - Session type (defaults to SESSION_TYPES.CREATE)
 * @param {string} [options.session.id] - Session ID for existing sessions
 * @param {Array<string>} [options.session.modalities] - Session-specific modalities (defaults to [MODALITIES.TEXT])
 * @param {Array<string>} [options.session.features] - Session-specific features (defaults to [FEATURES.REPORTS])
 * @param {Array<Object>} [options.interactions=[]] - Array of interaction objects
 * @param {string} [options.interactions[].event_id] - Unique event identifier for the interaction
 * @param {string} [options.interactions[].type] - Interaction type (defaults to 'conversation.item.create')
 * @param {Object} [options.interactions[].item] - Interaction item details
 * @param {string} [options.interactions[].item.id] - Unique item identifier
 * @param {string} [options.interactions[].item.type] - Item type (defaults to INTERACTION_TYPES.MESSAGE)
 * @param {string} [options.interactions[].item.role] - Role of the interaction (defaults to 'user')
 * @param {Object} [options.interactions[].item.content] - Content of the interaction
 * @param {Function} [options.setSession] - Callback for session updates, receives session object
 * @param {Function} options.setText - Required callback for text updates, receives {text, responseId}
 * @param {Function} [options.setData] - Callback for data/view updates, receives {target, view}
 * @param {Function} [options.onFinish] - Callback when response is complete, receives {text}
 * @param {Function} [options.onError] - Callback for error handling
 * @param {Function} [options.onAction] - Callback for action completion, receives action object
 * @param {Function} [options.onSetReader] - Callback to receive the stream reader instance
 * @returns {Promise<string|void>} Promise resolving to response text or void on error/cancellation
 * @example
 * // Basic usage
 * getCopilotStreamAnswer({
 *   interactions: [{
 *     item: {
 *       content: { text: 'Generate a report for sales data' }
 *     }
 *   }],
 *   setText: ({ text, responseId }) => {
 *     console.log('Streaming text:', text);
 *   },
 *   onFinish: ({ text }) => {
 *     console.log('Final response:', text);
 *   }
 * });
 *
 * @example
 * // Advanced usage with session and features
 * getCopilotStreamAnswer({
 *   session: {
 *     id: 'existing-session-123',
 *     type: SESSION_TYPES.UPDATE,
 *     features: [FEATURES.REPORTS, FEATURES.KNOWLEDGE_BASE],
 *     modalities: [MODALITIES.TEXT, MODALITIES.VIEW]
 *   },
 *   interactions: [{
 *     item: {
 *       role: 'user',
 *       content: { text: 'Analyze the Q4 performance metrics' }
 *     }
 *   }],
 *   setSession: (session) => console.log('Session updated:', session),
 *   setText: ({ text }) => updateUI(text),
 *   setData: ({ target, view }) => renderDataView(target, view),
 *   onAction: (action) => handleAction(action),
 *   onError: () => showErrorMessage(),
 *   onSetReader: (reader) => setStreamReader(reader)
 * });
 */
export const getCopilotStreamAnswer = _ref3 => {
  let {
    session = {},
    interactions = [],
    setSession,
    setText,
    setData,
    onFinish,
    onError,
    onAction,
    onArtifact,
    onSetReader
  } = _ref3;
  return new Promise(resolve => {
    var _session$modalities, _session$features;
    if (!interactions.length) return resolve();

    // Build session object
    const finalSession = {
      event_id: (session === null || session === void 0 ? void 0 : session.event_id) || uuid(),
      type: (session === null || session === void 0 ? void 0 : session.type) || SESSION_TYPES.CREATE,
      session: {
        id: session.id || null,
        modalities: (_session$modalities = session.modalities) !== null && _session$modalities !== void 0 && _session$modalities.length ? session.modalities : [MODALITIES.TEXT],
        features: (_session$features = session.features) !== null && _session$features !== void 0 && _session$features.length ? session.features : [FEATURES.REPORTS]
      }
    };

    // Build interactions array of objects
    const finalInteractions = interactions.map(interaction => {
      var _interaction$item, _interaction$item2, _interaction$item3, _interaction$item4;
      return {
        event_id: (interaction === null || interaction === void 0 ? void 0 : interaction.event_id) || uuid(),
        type: (interaction === null || interaction === void 0 ? void 0 : interaction.type) || 'conversation.item.create',
        item: {
          id: (interaction === null || interaction === void 0 ? void 0 : (_interaction$item = interaction.item) === null || _interaction$item === void 0 ? void 0 : _interaction$item.id) || uuid(),
          type: (interaction === null || interaction === void 0 ? void 0 : (_interaction$item2 = interaction.item) === null || _interaction$item2 === void 0 ? void 0 : _interaction$item2.type) || INTERACTION_TYPES.MESSAGE,
          role: (interaction === null || interaction === void 0 ? void 0 : (_interaction$item3 = interaction.item) === null || _interaction$item3 === void 0 ? void 0 : _interaction$item3.role) || 'user',
          content: (interaction === null || interaction === void 0 ? void 0 : (_interaction$item4 = interaction.item) === null || _interaction$item4 === void 0 ? void 0 : _interaction$item4.content) || {}
        }
      };
    });

    // Standard response creation object
    const extra = {
      event_id: uuid(),
      type: 'response.create'
    };
    const body = [finalSession, ...finalInteractions, extra];
    Fetch('/analyzer/v1/copilot?format=web', {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream'
      },
      body: JSON.stringify(body)
    }).then(response => {
      if (response.status !== 200) {
        onError === null || onError === void 0 ? void 0 : onError();
        resolve();
      } else {
        readStream({
          resolve,
          response,
          setSession,
          setText,
          setData,
          onFinish,
          onError,
          onAction,
          onArtifact,
          onSetReader
        });
      }
    }).catch(err => {
      console.error('Copilot stream request error:', err);
      onError === null || onError === void 0 ? void 0 : onError();
      resolve();
    });
  });
};